/**
 * Background Task Manager
 * Allows long-running tasks (like Google Calendar Callout Scheduling and Sales Pipeline Verification)
 * to execute non-blockingly in the background so the user can carry on with their work immediately.
 * Dispatches live updates to UI toasts and surfaces prominent alerts if any task fails.
 */

class BackgroundTaskManager {
  constructor() {
    this.tasks = new Map();
    this.listeners = new Set();
  }

  getTasks() {
    return Array.from(this.tasks.values()).sort((a, b) => b.createdAt - a.createdAt);
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getTasks());
    return () => this.listeners.delete(listener);
  }

  notify() {
    const taskList = this.getTasks();
    this.listeners.forEach((listener) => {
      try {
        listener(taskList);
      } catch (err) {
        console.warn('[BackgroundTaskManager] Listener error:', err);
      }
    });
  }

  /**
   * Enqueues and begins execution of an asynchronous background task.
   */
  runTask({
    id = `task_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    title = 'Background Task',
    type = 'general',
    run,
    successMessage = 'Completed successfully',
    actionLabel = null,
    getActionLink = null,
    onSuccess = null,
    onError = null,
    payload = null
  }) {
    if (typeof run !== 'function') {
      throw new Error('runTask requires a run function');
    }

    const taskEntry = {
      id,
      title,
      type,
      status: 'running', // 'running' | 'completed' | 'failed'
      progressMessage: 'Processing in background...',
      successMessage,
      actionLabel,
      actionLink: null,
      error: null,
      payload,
      runFn: run,
      createdAt: Date.now(),
      completedAt: null
    };

    this.tasks.set(id, taskEntry);
    this.notify();

    // Execute asynchronously (fire-and-monitor)
    (async () => {
      try {
        const result = await run();
        const existing = this.tasks.get(id);
        if (!existing) return;

        existing.status = 'completed';
        existing.result = result;
        existing.completedAt = Date.now();
        if (typeof getActionLink === 'function') {
          existing.actionLink = getActionLink(result);
        }

        if (typeof onSuccess === 'function') {
          try {
            onSuccess(result);
          } catch (cbErr) {
            console.warn('[BackgroundTaskManager] onSuccess callback error:', cbErr);
          }
        }

        this.notify();

        // Auto-dismiss completed successful tasks after 5.5 seconds
        setTimeout(() => {
          const t = this.tasks.get(id);
          if (t && t.status === 'completed') {
            this.tasks.delete(id);
            this.notify();
          }
        }, 5500);
      } catch (err) {
        console.error(`[BackgroundTaskManager] Task "${title}" failed:`, err);
        const existing = this.tasks.get(id);
        if (!existing) return;

        existing.status = 'failed';
        existing.error = err?.message || String(err) || 'An unexpected error occurred.';
        existing.completedAt = Date.now();

        if (typeof onError === 'function') {
          try {
            onError(err);
          } catch (cbErr) {
            console.warn('[BackgroundTaskManager] onError callback error:', cbErr);
          }
        }

        this.notify();
      }
    })();

    return id;
  }

  retryTask(taskId) {
    const task = this.tasks.get(taskId);
    if (!task || !task.runFn) return;

    task.status = 'running';
    task.error = null;
    task.createdAt = Date.now();
    this.notify();

    (async () => {
      try {
        const result = await task.runFn();
        task.status = 'completed';
        task.result = result;
        task.completedAt = Date.now();
        this.notify();

        setTimeout(() => {
          if (this.tasks.get(taskId)?.status === 'completed') {
            this.tasks.delete(taskId);
            this.notify();
          }
        }, 5500);
      } catch (err) {
        console.error(`[BackgroundTaskManager] Retry for "${task.title}" failed:`, err);
        task.status = 'failed';
        task.error = err?.message || String(err) || 'Retry failed.';
        task.completedAt = Date.now();
        this.notify();
      }
    })();
  }

  dismissTask(taskId) {
    if (this.tasks.has(taskId)) {
      this.tasks.delete(taskId);
      this.notify();
    }
  }

  clearCompleted() {
    let changed = false;
    for (const [id, t] of this.tasks.entries()) {
      if (t.status === 'completed') {
        this.tasks.delete(id);
        changed = true;
      }
    }
    if (changed) this.notify();
  }
}

export const backgroundTaskManager = new BackgroundTaskManager();
export default backgroundTaskManager;
